/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import BackButton from '../common/BackButton';
import './MonthlyBidSummary.css';

interface BidSummary {
  bidId: number;
  bidName: string;
  unmodifiedCostTotal: number;
  discountPercentage: number | null;
  discountFlat: number | null;
  margin: number | null;
  submittedCostTotal: number;
  dueDate: string;
  submitted: boolean;
  accepted: boolean;
  authorName: string;
}

type SortColumn = 'bidName' | 'authorName' | 'dueDate' | 'unmodifiedCostTotal' | 'discountPercentage' | 'discountFlat' | 'margin' | 'submittedCostTotal' | 'status';
type SortDirection = 'asc' | 'desc';

const MonthlyBidSummary: React.FC = () => {
  const { year, month, category } = useParams<{ year: string; month: string; category: string }>();
  const navigate = useNavigate();
  const [bids, setBids] = useState<BidSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [monthName, setMonthName] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [totalUnmodifiedCost, setTotalUnmodifiedCost] = useState<number>(0);
  const [totalSubmittedCost, setTotalSubmittedCost] = useState<number>(0);
  const [sortColumn, setSortColumn] = useState<SortColumn>('dueDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    if (year && month && category) {
      fetchBidSummary();
    }
  }, [year, month, category]);

  const fetchBidSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getExecutiveMonthlyBidSummary(
        parseInt(year!),
        parseInt(month!),
        category!
      );

      if (response.success && response.bids) {
        setBids(response.bids);
        setMonthName(response.monthName || '');
        setCompanyName(response.companyName || '');
        setTotalUnmodifiedCost(response.totalUnmodifiedCost || 0);
        setTotalSubmittedCost(response.totalSubmittedCost || 0);
      } else {
        setError(response.error || 'Failed to load bid summary');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'An error occurred while fetching bid summary');
      console.error('Error fetching bid summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBidClick = (bidId: number) => {
    navigate(`/executive/bid/${bidId}`);
  };

  const formatCurrency = (value: number | null): string => {
    if (value === null || value === undefined) return '$0.00';
    return `$${value.toFixed(2)}`;
  };

  const getCategoryLabel = (): string => {
    switch (category) {
      case 'submitted':
        return 'Submitted Bids';
      case 'accepted':
        return 'Accepted Bids';
      case 'not_accepted':
        return 'Not Accepted Bids';
      default:
        return 'All Bids';
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'No date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getStatusValue = (bid: BidSummary): number => {
    if (bid.accepted) return 2;
    if (bid.submitted) return 1;
    return 0;
  };

  const sortedBids = [...bids].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortColumn) {
      case 'bidName':
        aValue = a.bidName.toLowerCase();
        bValue = b.bidName.toLowerCase();
        break;
      case 'authorName':
        aValue = a.authorName.toLowerCase();
        bValue = b.authorName.toLowerCase();
        break;
      case 'dueDate':
        aValue = new Date(a.dueDate).getTime();
        bValue = new Date(b.dueDate).getTime();
        break;
      case 'unmodifiedCostTotal':
        aValue = a.unmodifiedCostTotal;
        bValue = b.unmodifiedCostTotal;
        break;
      case 'discountPercentage':
        aValue = a.discountPercentage ?? 0;
        bValue = b.discountPercentage ?? 0;
        break;
      case 'discountFlat':
        aValue = a.discountFlat ?? 0;
        bValue = b.discountFlat ?? 0;
        break;
      case 'margin':
        aValue = a.margin ?? 0;
        bValue = b.margin ?? 0;
        break;
      case 'submittedCostTotal':
        aValue = a.submittedCostTotal;
        bValue = b.submittedCostTotal;
        break;
      case 'status':
        aValue = getStatusValue(a);
        bValue = getStatusValue(b);
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getSortIndicator = (column: SortColumn): string => {
    if (sortColumn !== column) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <div className="monthly-bid-summary">
      <BackButton to="/executive/report" label="Back to Report" />
      <div className="summary-header">
        <h1>Bid Summary - {monthName} {year}</h1>
        {companyName && <h2>{companyName}</h2>}
        <h3 className="category-label">{getCategoryLabel()}</h3>
      </div>

      <div className="summary-totals">
        <div className="total-card">
          <div className="total-label">Total Unmodified Cost</div>
          <div className="total-value">{formatCurrency(totalUnmodifiedCost)}</div>
        </div>
        <div className="total-card">
          <div className="total-label">Total Submitted Cost</div>
          <div className="total-value submitted">{formatCurrency(totalSubmittedCost)}</div>
        </div>
        <div className="total-card">
          <div className="total-label">Total Bids</div>
          <div className="total-value">{bids.length}</div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading bid summary...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : bids.length === 0 ? (
        <div className="no-bids">No bids found for this period and category.</div>
      ) : (
        <div className="bids-table-container">
          <table className="bids-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('bidName')} className="sortable">
                  Bid Name{getSortIndicator('bidName')}
                </th>
                <th onClick={() => handleSort('authorName')} className="sortable">
                  Author{getSortIndicator('authorName')}
                </th>
                <th onClick={() => handleSort('dueDate')} className="sortable">
                  Due Date{getSortIndicator('dueDate')}
                </th>
                <th onClick={() => handleSort('unmodifiedCostTotal')} className="sortable">
                  Unmodified Cost{getSortIndicator('unmodifiedCostTotal')}
                </th>
                <th onClick={() => handleSort('discountPercentage')} className="sortable">
                  Discount %{getSortIndicator('discountPercentage')}
                </th>
                <th onClick={() => handleSort('discountFlat')} className="sortable">
                  Discount ${getSortIndicator('discountFlat')}
                </th>
                <th onClick={() => handleSort('margin')} className="sortable">
                  Margin %{getSortIndicator('margin')}
                </th>
                <th onClick={() => handleSort('submittedCostTotal')} className="sortable">
                  Submitted Cost{getSortIndicator('submittedCostTotal')}
                </th>
                <th onClick={() => handleSort('status')} className="sortable">
                  Status{getSortIndicator('status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedBids.map((bid) => (
                <tr
                  key={bid.bidId}
                  onClick={() => handleBidClick(bid.bidId)}
                  className="bid-row"
                >
                  <td className="bid-name-cell">{bid.bidName}</td>
                  <td>{bid.authorName}</td>
                  <td>{formatDate(bid.dueDate)}</td>
                  <td className="currency">{formatCurrency(bid.unmodifiedCostTotal)}</td>
                  <td className="percent">
                    {bid.discountPercentage ? `${bid.discountPercentage.toFixed(2)}%` : '-'}
                  </td>
                  <td className="currency">
                    {bid.discountFlat ? formatCurrency(bid.discountFlat) : '-'}
                  </td>
                  <td className="percent">
                    {bid.margin ? `${bid.margin.toFixed(2)}%` : '-'}
                  </td>
                  <td className="currency submitted-cost">{formatCurrency(bid.submittedCostTotal)}</td>
                  <td>
                    {bid.accepted && <span className="status-badge accepted">Accepted</span>}
                    {bid.submitted && !bid.accepted && <span className="status-badge submitted">Submitted</span>}
                    {!bid.submitted && <span className="status-badge">Not Submitted</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MonthlyBidSummary;
